// @ts-nocheck
// Call Stats — Recent Call Log table: columns + DataTable, split out of the
// main panel. Direction/status badges restyled as flat Tailwind pills on
// the app's own semantic tokens instead of the old `agent-call-stats-
// direction`/`agent-call-stats-status` cti.css classes.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { FiPhoneIncoming, FiPhoneOutgoing } from 'react-icons/fi';
import { Eye, Phone, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DataTable } from '@/components/data-table/DataTable';
import { downloadXlsx, exportLines, stampedFileName } from '@/components/data-table/csv-export';
import {
  CallTranscriptButton,
  loadTranscriptAvailability,
  useCanViewTranscripts,
  useTranscriptAvailability,
} from '@/features/transcripts/CallTranscript';
import MediaAudioList, { mediaUrl } from '@/components/partials/components/MediaAudioList';
import { canReadRecordings, isAgentVoicemailEnabled } from '@/utilities/recordingsAcl';
import { canCallQualityFeedback, FeedbackDetail, feedbackExportColumns, feedbackOf } from '@/features/call-quality-feedback/feedback';
import CopyButton from '@/features/api-webhook/components/CopyButton';
import CallQualityDetail from '@/features/ivr-reports/components/CallQualityDetail';
import CdrCallDetailDialog from '@/features/ivr-reports/components/CdrCallDetailDialog';
import { getUser } from '@/utilities/setting';
import { toast } from 'sonner';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_DISPOSITION_BY_CALL, API_DISPOSITIONS_BY_CALLS, API_HANGUP_CAUSES } from '@/service/url';
import { formatDuration } from './format';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { callDurationSeconds, callOutcome, callOutcomeEvt } from '../../ctiHelpers';
import { CALL_OUTCOME_LABELS } from '@/utilities/callStatus';
import { withAdditionalDetails } from '@/features/ivr-reports/call-report/callReportColumns';
import { useCustomFieldsVisible } from '@/features/contact-group/custom-fields/useCustomFieldsVisible';

function Pill({ tone, children }: any) {
  const toneClass =
    tone === 'primary'
      ? 'bg-primary/10 text-primary'
      : tone === 'good'
        ? 'bg-success/10 text-success'
        : tone === 'warn'
          ? 'bg-warning/10 text-warning'
          : tone === 'crit'
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${toneClass}`}>
      {children}
    </span>
  );
}

// Recording / Voicemail as main columns (same cell as the follow-ups panel).
const mediaCol = (kind, header) => ({
  id: kind,
  accessorFn: (call) => ((call?.media || []).some((item) => item?.kind === kind) ? header : ''),
  header,
  enableSorting: true,
  cell: ({ row }) => (
    <MediaAudioList media={(row.original?.media || []).filter((item) => item?.kind === kind)} compact />
  ),
});

const CALL_TYPE_FLAGS = [
  ['is_transfer', 'Transfer'],
  ['is_conference', 'Conference'],
  ['is_ivr', 'IVR'],
  ['is_agent_lead', 'Agent Lead'],
  ['is_whisper', 'Whisper'],
  ['is_monitor', 'Monitor'],
  ['is_barge_in', 'Barge In'],
  ['is_follow_up', 'Follow-up'],
  ['is_voicemail', 'Voicemail'],
  ['is_missed_ivr', 'Missed IVR'],
];

function callTypeTags(call) {
  return [
    isIncomingCall(call) ? 'Inbound' : 'Outbound',
    ...CALL_TYPE_FLAGS.filter(([flag]) => call?.[flag]).map(([, label]) => label),
  ];
}

const isIncomingCall = (call) => {
  const dir = String(call?.direction || '').toLowerCase();
  return dir === 'incoming' || dir === 'inbound' || dir === 'in';
};

const CALL_STATUS = {
  connected: ['Answered', 'good'],
  failed: ['Failed', 'crit'],
  abandoned: ['Abandoned', 'warn'],
};

const callStatusLabel = (call) => CALL_OUTCOME_LABELS[callOutcome(call)];

const StatusPill = ({ label }) => (
  <span className={`call-evt call-evt--static call-evt--${callOutcomeEvt(label)}`}>{label}</span>
);

// Call duration = stored total_duration; older rows keep the billing rule (0 when the other party never connected).
const talkSeconds = (call) =>
  call.total_duration != null && call.total_duration !== ''
    ? callDurationSeconds(call)
    : call.other_party_call_status && call.other_party_call_status !== 'connected'
      ? 0
      : Number(call.billing_duration) || 0;

const capitalize = (v) => {
  const s = String(v || '');
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

const durationCol = (id, accessorKey, header) => ({
  id,
  accessorKey,
  header,
  // formatDuration turns missing / invalid values into 0s.
  cell: ({ getValue }) => formatDuration(getValue()),
  enableSorting: true,
});

const idCol = (id, accessorKey, header) => ({
  id,
  accessorKey,
  header,
  cell: ({ getValue }) => {
    const value = getValue();
    if (!value) return '-';
    return (
      <span className="inline-flex items-center gap-1">
        <span className="block max-w-[180px] truncate font-mono text-[11px]" title={String(value)}>
          {value}
        </span>
        <CopyButton text={String(value)} className="h-6 w-6 shrink-0 p-0" />
      </span>
    );
  },
  enableSorting: true,
});

const SEARCHABLE_COLUMN_IDS = new Set(['call_sid', 'trace_id', 'did', 'agent_name']);

const textCol = (id, accessorKey, header, fallback = '-') => ({
  id,
  accessorKey,
  header,
  cell: ({ getValue }) => getValue() || fallback,
  enableSorting: true,
});

const secs = (v) => formatDuration(Number(v) > 0 ? Number(v) : 0);
const at = (v) => (v ? moment(v).format('DD-MM-YYYY HH:mm:ss') : '-');
const fmtFieldLabel = (key) => String(key).replace(/[_-]+/g, ' ').trim();
const fmtFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map(fmtFieldValue).join(', ') : '-';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return '-'; }
  }
  return String(value);
};

function legStatus(status) {
  const known = CALL_STATUS[status];
  return known ? <StatusPill label={known[0]} /> : '-';
}

function DetailCard({ title, items, children }: any) {
  return (
    <section className="rounded-xl border border-border/60 bg-card p-3">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {items ? (
        <dl className="m-0 grid gap-x-4 gap-y-1 text-xs [grid-template-columns:minmax(110px,auto)_1fr]">
          {items.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="m-0 min-w-0 break-words font-medium text-foreground">
                {value === null || value === undefined || value === '' ? '-' : value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
      {children}
    </section>
  );
}

function DispositionBody({ d }: any) {
  const fields = Object.entries(d?.data && typeof d.data === 'object' ? d.data : {});
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span>Form: <span className="font-medium text-foreground">{d.disposition_form_name || '-'}</span></span>
        <span>Submitted: <span className="font-medium text-foreground">{at(d.created_at)}</span></span>
        {d.edited_at ? <span>Edited: <span className="font-medium text-foreground">{at(d.edited_at)}</span></span> : null}
        {d.is_auto_submit ? <span className="font-medium text-warning">Auto-submitted</span> : null}
      </div>
      {fields.length ? (
        <dl className="m-0 grid max-h-72 grid-cols-1 gap-y-1.5 overflow-y-auto">
          {fields.map(([key, value]) => (
            <div key={key} className="min-w-0">
              <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{fmtFieldLabel(key)}</dt>
              <dd className="m-0 break-words text-xs text-foreground">{fmtFieldValue(value)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="text-xs text-muted-foreground">No fields saved for this disposition.</div>
      )}
    </div>
  );
}

function DispositionCell({ call, prefetched, batchLoading }: any) {
  if (batchLoading) return <span className="text-xs text-muted-foreground">…</span>;
  if (prefetched === null) return <span className="text-xs text-muted-foreground">None</span>;
  if (prefetched) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="View disposition"
            className="max-w-[160px] truncate rounded-md border border-success/40 bg-success/10 px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-success/20"
          >
            {prefetched.disposition_form_name || 'Saved'}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3">
          <DispositionBody d={prefetched} />
        </PopoverContent>
      </Popover>
    );
  }
  return <DispositionFetchCell call={call} />;
}

function DispositionFetchCell({ call }: any) {
  const [state, setState] = useState<{ status: string; data: any }>({ status: 'idle', data: null });
  const load = () => {
    setState({ status: 'loading', data: null });
    const q = new URLSearchParams();
    if (call.call_sid) q.set('call_sid', call.call_sid);
    if (call.trace_id) q.set('trace_id', call.trace_id);
    getApiCall(`${API_DISPOSITION_BY_CALL}?${q}`)
      .then((data) => setState({ status: 'done', data: data || null }))
      .catch(() => setState({ status: 'error', data: null }));
  };
  const d = state.data;

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) load();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
        >
          View
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3">
        {state.status === 'loading' || state.status === 'idle' ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : state.status === 'error' ? (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-destructive">Could not load disposition</span>
            <button
              type="button"
              onClick={load}
              className="ml-auto rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground hover:bg-muted"
            >
              Retry
            </button>
          </div>
        ) : !d ? (
          <div className="text-xs text-muted-foreground">No disposition saved</div>
        ) : (
          <DispositionBody d={d} />
        )}
      </PopoverContent>
    </Popover>
  );
}

function CallAudioFeedbackDetail({ call }: any) {
  const [state, setState] = useState<{ status: string; list: any[] }>({ status: 'idle', list: [] });
  const load = () => {
    setState({ status: 'loading', list: [] });
    postApiCall(API_DISPOSITIONS_BY_CALLS, {
      calls: [{ call_sid: call.call_sid || null, trace_id: call.trace_id || null }],
      include_call_quality_feedback: true,
    })
      .then((rows) => setState({ status: 'done', list: feedbackOf(Array.isArray(rows) ? rows[0] : null) }))
      .catch(() => setState({ status: 'error', list: [] }));
  };
  return (
    <DetailCard title="Call audio feedback">
      {state.status === 'done' ? (
        <FeedbackDetail list={state.list} />
      ) : state.status === 'loading' ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : (
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground hover:bg-muted"
          >
            {state.status === 'error' ? 'Retry' : 'View call audio feedback'}
          </button>
          {state.status === 'error' ? <span className="text-destructive">Could not load feedback</span> : null}
        </div>
      )}
    </DetailCard>
  );
}

function CallDetailRow({ call, onView }: any) {
  const showCustomFields = useCustomFieldsVisible();
  const incoming = isIncomingCall(call);
  const otherParty = incoming ? 'Caller' : 'Callee';
  const qualityAltIds = useMemo(() => [call.trace_id], [call.trace_id]);
  return (
    // sticky: stays in view when the wide table is scrolled sideways
    <div className="sticky left-0 max-w-7xl space-y-3 p-4">
      {call?.trace_id ? (
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2" onClick={() => onView(call)}>
          <Eye size={14} /> View call details
        </Button>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <DetailCard
          title="Call Legs"
          items={[
            [
              'Agent call status',
              legStatus(call.call_status),
            ],
            ['Agent ring', secs(call.bridge_ring_duration)],
            [`${otherParty} call status`, legStatus(call.other_party_call_status)],
            [`${otherParty} ring`, secs(call.other_party_ring_duration)],
            ['Hangup cause', capitalize(call.hangup_cause)],
          ]}
        />
        <DetailCard
          title="Timing"
          items={[
            // ['Start', at(call.start_time)],
            // ['Answered', at(call.bridge_time)],
            // ['End', at(call.end_time)],
            ['Call duration', secs(talkSeconds(call))],
            ['Talk time', secs(call.patch_duration)],
            ['Customer on hold', secs(call.hold_duration)],
            ['Agent muted', secs(call.mute_duration)],
            ['Agent wrap-up', secs(call.wrap_up_seconds)],
          ]}
        />
        <DetailCard
          title="Customer & Context"
          items={[
            ['Customer number', call.number],
            ['Contact', call.caller_profile],
            // The contact's custom fields (template + added fields).
            // One line per matched contact; contacts without custom fields come as empty lines.
            ...(showCustomFields
              ? [['Customer additional details', String(call.customer_additional_details || '').split('\n').filter(Boolean).join('\n')]]
              : []),
            // Circle / operator hidden for now.
            // ['Circle', call.circle_name],
            // ['Operator', call.operator_name],
            ['DID', call.did],
            ['Campaign', call.campaign_name || 'No campaign'],
            ['Call group', call.call_group_name || 'No call group'],
          ]}
        />
      </div>
      <div className="flex flex-col gap-3 md:flex-row">
        {call.trace_id && canCallQualityFeedback('read') ? (
          <div className="min-w-0 flex-1">
            <CallAudioFeedbackDetail call={call} />
          </div>
        ) : null}
        <div className="min-w-0 flex-1 empty:hidden [&>.call-quality-detail]:!m-0 [&>.call-quality-detail]:bg-card">
          <CallQualityDetail callSid={call.call_sid} altIds={qualityAltIds} />
        </div>
      </div>
    </div>
  );
}

// Moved into the expanded row; still toggleable from Columns.
const HIDDEN_BY_DEFAULT = {
  caller_profile: false,
  campaign_name: false,
  call_group_name: false,
};

const dispoKey = (call) => `${call?.call_sid || ''}|${call?.trace_id || ''}`;

const DURATION_PARTS = [
  ['Agent ring', 'bridge_ring_duration', "Time the agent's phone rang before answering"],
  ['Customer on hold', 'hold_duration', 'Time the agent kept the customer on hold'],
  ['Agent muted', 'mute_duration', "Time the agent's mic was muted"],
  ['Agent wrap-up', 'wrap_up_seconds', 'After-call time the agent spent on wrap-up'],
];

// showAgent: team-lead view (rows from several agents) — adds an Agent column + export field.
export default function CallLogTable({
  rows: rawRows,
  loading,
  showAgent = false,
  tableId = 'cti.agent-call-stats.recent-calls.v2',
  exportName = 'calls', // file name prefix; a timestamp is added per export
  onCallNumber = null, // agent's own log: call icon next to the number (backend re-checks before dialing)
  callBlockedReason = null,
  // Server-paged mode: rows are one page. serverPaging = { pageIndex, pageSize, rowCount, onChange };
  // onSearchChange gets the debounced search text (searched server-side, back to page 1);
  // fetchExportRows() resolves { rows, total, fetched } for the whole filtered log.
  serverPaging = null,
  onSearchChange = null,
  fetchExportRows = null,
  exportLabel = 'Export',
}: any) {
  const [searchText, setSearchText] = usePersistedState(`cti/CallLogTable/${tableId}:searchText`, '');
  useEffect(() => {
    if (!onSearchChange) return undefined;
    const t = setTimeout(() => onSearchChange(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText, onSearchChange]);
  const exportingRef = useRef(false);

  const rows = rawRows;
  const canViewRecordings = canReadRecordings();
  const canViewVoicemails = useMemo(() => isAgentVoicemailEnabled(), []);
  const canViewTranscripts = useCanViewTranscripts();

  const transcriptLineCount = useTranscriptAvailability(
    useMemo(() => (rows || []).map((row) => row?.trace_id), [rows])
  );

  const [dispoBatch, setDispoBatch] = useState<{ loading: boolean; map: Map<any, any> | null }>({ loading: false, map: null });
  const pagedRows = serverPaging ? rows : null;
  useEffect(() => {
    const list = (pagedRows || []).filter((c) => c?.call_sid || c?.trace_id);
    if (!list.length) {
      setDispoBatch({ loading: false, map: pagedRows ? new Map() : null });
      return undefined;
    }
    let cancelled = false;
    setDispoBatch((prev) => ({ ...prev, loading: !prev.map || list.some((c) => !prev.map.has(dispoKey(c))) }));
    postApiCall(API_DISPOSITIONS_BY_CALLS, {
      calls: list.map((c) => ({ call_sid: c.call_sid || null, trace_id: c.trace_id || null })),
    })
      .then((resp) => {
        if (cancelled) return;
        const out = Array.isArray(resp) ? resp : [];
        setDispoBatch({ loading: false, map: new Map(list.map((c, i) => [dispoKey(c), out[i]?.id ? out[i] : null])) });
      })
      .catch(() => !cancelled && setDispoBatch({ loading: false, map: null }));
    return () => {
      cancelled = true;
    };
  }, [pagedRows]);

  const [detailCall, setDetailCall] = useState<any>(null);
  const [hangupCauseMap, setHangupCauseMap] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (!detailCall || hangupCauseMap) return;
    getApiCall(API_HANGUP_CAUSES)
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : resp?.data || [];
        setHangupCauseMap(
          Object.fromEntries(
            list.map((item: any) => [item.hangup_cause_code, item.message || item.hangup_cause_reason || '']),
          ),
        );
      })
      .catch(() => setHangupCauseMap({}));
  }, [detailCall, hangupCauseMap]);
  const detailRow = useMemo(
    () =>
      detailCall && {
        ...detailCall,
        call_type: isIncomingCall(detailCall) ? 'incoming' : 'outgoing',
        start_time: detailCall.start_time || detailCall.time_of_call,
      },
    [detailCall],
  );

  const columns = useMemo(
    () => [
      ...(showAgent ? [textCol('agent_name', 'agent_name', 'Agent')] : []),
      {
        id: 'direction',
        accessorFn: (call) =>
          [isIncomingCall(call) ? 'Incoming' : 'Outgoing', call.agent_profile_type].filter(Boolean).join(', '),
        header: 'Call',
        cell: ({ row }) => {
          const call = row.original;
          const isIncoming = isIncomingCall(call);
          return (
            <span className="inline-flex flex-wrap items-center gap-1">
              <Pill tone={isIncoming ? 'primary' : 'good'}>
                {isIncoming ? <FiPhoneIncoming size={12} /> : <FiPhoneOutgoing size={12} />}
                {isIncoming ? 'Incoming' : 'Outgoing'}
              </Pill>
              {call.agent_profile_type ? <Pill tone="primary">{call.agent_profile_type}</Pill> : null}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        id: 'call_type',
        accessorFn: (call) => callTypeTags(call).join(', '),
        header: 'Call Type',
        cell: ({ row }) => {
          const tags = callTypeTags(row.original);
          return tags.length ? (
            <span className="inline-flex flex-wrap items-center gap-1">
              {tags.map((tag) => (
                <Pill key={tag}>{tag}</Pill>
              ))}
            </span>
          ) : (
            '-'
          );
        },
        enableSorting: true,
      },
      {
        id: 'number',
        accessorKey: 'number',
        header: 'Customer Number',
        enableSorting: true,
        cell: ({ row }) => {
          const call = row.original;
          // dial_number is only sent for the agent's own callable rows; the click asks the
          // backend again (by call id) so break / call / disposition / login are DB-checked.
          if (!onCallNumber || showAgent || !call?.id || !call?.dial_number) return call?.number || '-';
          return (
            <span className="inline-flex items-center gap-1.5">
              {call.number || '-'}
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-6 w-6"
                disabled={Boolean(callBlockedReason)}
                title={callBlockedReason || 'Call this number'}
                aria-label="Call this number"
                onClick={(e) => {
                  e.stopPropagation();
                  onCallNumber(call);
                }}
              >
                <Phone size={12} />
              </Button>
            </span>
          );
        },
      },
      { id: 'caller_profile', accessorKey: 'caller_profile', header: 'Contact', enableSorting: true },
      {
        id: 'status',
        accessorFn: (call) => callStatusLabel(call),
        header: 'Call Status',
        cell: ({ row }) => <StatusPill label={callStatusLabel(row.original)} />,
        enableSorting: true,
      },
      // Agent leg status: outbound ~always Answered.
      // Hidden as redundant; calls.call_status still stored.
      // {
      //   id: 'agent_call_status',
      //   accessorFn: (call) => CALL_STATUS[call.call_status]?.[0] || '',
      //   header: 'Agent Status',
      //   cell: ({ getValue }) => {
      //     const label = getValue();
      //     return label ? <StatusPill label={label} /> : '-';
      //   },
      //   enableSorting: true,
      // },
      {
        id: 'duration',
        accessorFn: talkSeconds,
        header: 'Call Duration',
        cell: ({ getValue }) => formatDuration(getValue()),
        enableSorting: true,
      },
      {
        id: 'time',
        accessorKey: 'time_of_call',
        header: 'Time',
        cell: ({ row }) => {
          const t = row.original.time_of_call || row.original.start_time;
          return t ? moment(t).format('DD-MM-YYYY HH:mm:ss') : '-';
        },
        enableSorting: true,
      },
      // durationCol('other_party_ring_duration', 'other_party_ring_duration', 'Other Party Ring'),
      // durationCol('patch_duration', 'patch_duration', 'Talk Time'),
      {
        id: 'durations',
        header: 'Durations',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="grid grid-cols-[auto_auto] gap-x-2 text-[11px] leading-4">
            {DURATION_PARTS.map(([label, key, hint]) => (
              <React.Fragment key={key}>
                <span className="whitespace-nowrap text-muted-foreground" title={hint}>{label}</span>
                <span className="font-medium text-foreground">{secs(row.original?.[key])}</span>
              </React.Fragment>
            ))}
          </div>
        ),
      },
      textCol('did', 'did', 'DID'),
      textCol('campaign_name', 'campaign_name', 'Campaign', 'No campaign'),
      textCol('call_group_name', 'call_group_name', 'Call Group', 'No call group'),
      {
        id: 'disposition',
        header: 'Disposition',
        enableSorting: false,
        cell: ({ row }) => (
          <DispositionCell
            call={row.original}
            batchLoading={dispoBatch.loading}
            prefetched={dispoBatch.map ? (dispoBatch.map.get(dispoKey(row.original)) ?? null) : undefined}
          />
        ),
      },
      mediaCol('recording', 'Recording'),
      mediaCol('voicemail', 'Voicemail'),
      {
        id: 'transcript',
        accessorFn: (call) => transcriptLineCount(call?.trace_id),
        header: 'Transcript',
        enableSorting: true,
        size: 90,
        cell: ({ row }) => {
          const lineCount = transcriptLineCount(row.original?.trace_id);
          if (!lineCount) return <span className="text-muted-foreground">-</span>;
          return (
            <CallTranscriptButton
              traceId={row.original?.trace_id}
              lineCount={lineCount}
              className="h-7 w-7"
            />
          );
        },
      },
      idCol('call_sid', 'call_sid', 'Call ID'),
      idCol('trace_id', 'trace_id', 'Trace ID'),
    ].map((column) => ({
      ...column,
      enableGlobalFilter: SEARCHABLE_COLUMN_IDS.has(column.id),
    })),
    [transcriptLineCount, showAgent, onCallNumber, callBlockedReason, dispoBatch]
  );
  const searchPlaceholder = showAgent ? 'Search agent, call ID, trace ID, DID...' : 'Search call ID, trace ID, DID...';
  const visibleColumns = columns.filter(
    (c) =>
      (c.id !== 'transcript' || canViewTranscripts) &&
      (c.id !== 'recording' || canViewRecordings) &&
      (c.id !== 'voicemail' || canViewVoicemails),
  );

  const exportCallLog = async (table) => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    try {
      await exportCalls(table);
    } finally {
      exportingRef.current = false;
    }
  };

  const exportCalls = async (table) => {
    let calls = table.getPrePaginationRowModel().rows.map(({ original }) => original);
    if (fetchExportRows) {
      const toastId = toast.loading('Preparing export…');
      try {
        const result = await fetchExportRows();
        calls = result.rows;
        // Off-page rows have no transcript lookup yet.
        if (canViewTranscripts) await loadTranscriptAvailability(calls.map((c) => c.trace_id));
        toast.dismiss(toastId);
        if (result.total > result.fetched) {
          toast.warning(`Exported the latest ${result.fetched} of ${result.total} calls. Narrow the date range to export the rest.`);
        }
      } catch {
        toast.dismiss(toastId);
        toast.error('Could not load the calls to export');
        return;
      }
    }
    let dispositions = null;
    const withFeedback = canCallQualityFeedback('read');
    try {
      dispositions = await postApiCall(API_DISPOSITIONS_BY_CALLS, {
        calls: calls.map((c) => ({ call_sid: c.call_sid || null, trace_id: c.trace_id || null })),
        ...(withFeedback ? { include_call_quality_feedback: true } : {}),
      });
    } catch {
      toast.error('Could not load dispositions; exporting without them');
    }
    const indexOf = new Map(calls.map((c, i) => [c, i]));
    const rowOf = (c) => (Array.isArray(dispositions) ? dispositions[indexOf.get(c)] : null);
    const dispoOf = (c) => (rowOf(c)?.id ? rowOf(c) : null);
    const dispoDetails = (d) =>
      Object.entries(d?.data && typeof d.data === 'object' ? d.data : {})
        .map(([key, value]) => `${fmtFieldLabel(key)}: ${fmtFieldValue(value)}`)
        .join('; ') || '-';
    const dispoSubmitted = (d) =>
      [at(d.created_at), d.edited_at ? `edited ${at(d.edited_at)}` : '', d.is_auto_submit ? 'auto-submitted' : '']
        .filter(Boolean)
        .join(', ');
    const mediaUrls = (call, kind) =>
      (call?.media || [])
        .filter((m) => m?.kind === kind)
        .map((m) => mediaUrl(m) || 'Not ready yet')
        .join(' | ') || '-';
    const n = (v) => (Number(v) > 0 ? Number(v) : 0);
    const when = (c) => c.time_of_call || c.start_time;
    const fields = [
      ['Call ID', (c) => c.call_sid || '-'],
      ['Trace ID', (c) => c.trace_id || '-'],
      ['Customer Number', (c) => c.number || '-'],
      // Each matched contact's details with its custom fields on the same line (as in reports);
      // just the name when the number matches no saved contact.
      ['Customer Details', (c) =>
        withAdditionalDetails(c.customer_details, c.customer_additional_details) ||
        (c.caller_profile ? `Name: ${c.caller_profile}` : '-')],
      ['Customer Call Status', (c) => CALL_STATUS[c.other_party_call_status]?.[0] || '-'],
      ['Customer Ring (s)', (c) => n(c.other_party_ring_duration)],
      ['Customer on Hold (s)', (c) => n(c.hold_duration)],
      ['Call Start Date', (c) => (when(c) ? moment(when(c)).format('DD-MM-YYYY') : '-')],
      ['Call Start Time', (c) => (when(c) ? moment(when(c)).format('HH:mm:ss') : '-')],
      ['Call Type', (c) => (isIncomingCall(c) ? 'Incoming' : 'Outgoing')],
      ['Agent Profile', (c) => c.agent_profile_type || '-'],
      ...CALL_TYPE_FLAGS.filter(([flag]) => calls.some((c) => c?.[flag])).map(([flag, label]) => [label, (c) => (c?.[flag] ? 'Yes' : 'No')]),
      ['DID', (c) => c.did || '-'],
      ['Campaign', (c) => c.campaign_name || 'No campaign'],
      ['Call Group', (c) => c.call_group_name || 'No call group'],
      ...(showAgent ? [['Agent', (c) => c.agent_name || '-']] : []),
      ['Agent Call Status', (c) => CALL_STATUS[c.call_status]?.[0] || '-'],
      ['Agent Ring (s)', (c) => n(c.bridge_ring_duration)],
      ['Agent Talk Time (s)', (c) => n(c.patch_duration)],
      ['Agent Muted (s)', (c) => n(c.mute_duration)],
      ['Agent Wrap-up (s)', (c) => n(c.wrap_up_seconds)],
      ['Agent Call Duration (s)', (c) => n(talkSeconds(c))],
      ['Call Status', (c) => callStatusLabel(c) || '-'],
      ['Hangup Cause', (c) => capitalize(c.hangup_cause) || '-'],
      ...(Array.isArray(dispositions)
        ? [
            ['Disposition Form', (c) => dispoOf(c)?.disposition_form_name || (dispoOf(c) ? '-' : 'No disposition')],
            ['Disposition Submitted', (c) => (dispoOf(c) ? dispoSubmitted(dispoOf(c)) : '-')],
            ['Disposition Details', (c) => (dispoOf(c) ? dispoDetails(dispoOf(c)) : '-')],
          ]
        : []),
      ...(canViewRecordings ? [['Recording', (c) => mediaUrls(c, 'recording')]] : []),
      ...(canViewVoicemails ? [['Voicemail', (c) => mediaUrls(c, 'voicemail')]] : []),
      ...(canViewTranscripts ? [['Transcript', (c) => (transcriptLineCount(c.trace_id) ? 'Yes' : 'No')]] : []),
      ...(withFeedback && Array.isArray(dispositions)
        ? Object.keys(feedbackExportColumns([])).map((label) => [label, (c) => feedbackExportColumns(feedbackOf(rowOf(c)))[label]])
        : []),
    ];
    await downloadXlsx(exportLines(fields, calls), stampedFileName(exportName, 'xlsx'));
  };

  return (
    <>
      <DataTable
        tableId={tableId}
        initialColumnVisibility={HIDDEN_BY_DEFAULT}
        renderSubRow={(call) => (
          <CallDetailRow call={call} onView={setDetailCall} />
        )}
        getRowId={(call, idx) => String(call?.id ?? call?.call_sid ?? idx)}
        columns={visibleColumns}
        data={rows}
        isLoading={loading}
        pageSize={10}
        emptyTitle="No calls"
        emptyDescription="No calls found for the selected date range"
        onExport={exportCallLog}
        exportLabel={exportLabel}
        searchPlaceholder={searchPlaceholder}
        {...(serverPaging ? { serverPagination: serverPaging } : {})}
        {...(onSearchChange
          ? {
              leftContent: (
                <div className="relative w-full max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="!pl-11"
                    style={{ paddingLeft: '2.75rem' }}
                  />
                </div>
              ),
            }
          : {})}
      />
      <CdrCallDetailDialog
        row={detailRow}
        did={detailRow?.did || '-'}
        pageRows={rows || []}
        userRole={getUser()?.role}
        hangupCauseMap={hangupCauseMap || {}}
        transcriptLines={detailRow ? transcriptLineCount(detailRow.trace_id) : 0}
        onClose={() => setDetailCall(null)}
      />
    </>
  );
}
